import * as PIXI from 'pixi.js'
type TextStyle = Partial<PIXI.ITextStyle>

interface TextConstructor {
    new(text?: string, style?: any): PIXI.Text | PIXI.BitmapText
}

export class ScalingTextField extends PIXI.Container {
    private readonly _content: PIXI.Text | PIXI.BitmapText

    private _minScale: number
    private _maxWidth: number

    private static normalizeFillColor(value: unknown): string {
        if (typeof value === 'string') {
            return value
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
            const hex = Math.max(0, Math.min(0xffffff, Math.floor(value)))
            return `#${hex.toString(16).padStart(6, '0')}`
        }

        return '#ffe718'
    }

    private static createContentWithFallback(constructor: TextConstructor, style: any): PIXI.Text | PIXI.BitmapText {
        try {
            return new constructor('', style)
        } catch (error) {
            const safeStyle = style && typeof style === 'object' ? { ...style } : {}

            if (Array.isArray(safeStyle.fill)) {
                safeStyle.fill = safeStyle.fill.map((color: unknown) => ScalingTextField.normalizeFillColor(color))
            } else if (safeStyle.fill !== undefined) {
                safeStyle.fill = ScalingTextField.normalizeFillColor(safeStyle.fill)
            }

            if (
                Array.isArray(safeStyle.fillGradientStops) &&
                Array.isArray(safeStyle.fill) &&
                safeStyle.fillGradientStops.length >= safeStyle.fill.length
            ) {
                safeStyle.fillGradientStops = safeStyle.fillGradientStops.slice(0, Math.max(0, safeStyle.fill.length - 1))
            }

            try {
                return new constructor('', safeStyle)
            } catch {
                const flatStyle = {
                    ...safeStyle,
                    fill: '#ffe718',
                }

                delete (flatStyle as any).fillGradientStops
                return new constructor('', flatStyle)
            }
        }
    }

    public constructor(minScale: number, maxWidth: number, constructor: TextConstructor, style: any = null) {
        super()

        this._content = ScalingTextField.createContentWithFallback(constructor, style)
        this._content.anchor.set(0.5)
        this.addChild(this._content)

        this._minScale = minScale
        this._maxWidth = maxWidth

        this.validateScale()
    }

    public setCentered(centered: boolean = true): void {
        if (centered && this.anchor) {
            this.anchor.set(0.5)
        }
    }

    protected validateScale(): void {
        this._content.scale.set(1.0)
        const scale = Math.min(this._maxWidth / this._content.width, this._minScale)
        this._content.scale.set(scale)
    }

    public changeContent(minScale: number, maxWidth: number, text: string): void {
        this._minScale = minScale
        this._content.text = text
        this._maxWidth = maxWidth

        this.validateScale()
    }

    public changeStyle(style: TextStyle) {
        (this._content as PIXI.Text).style = style
    }

    public get content(): PIXI.Text | PIXI.BitmapText {
        return this._content
    }

    public set minScale(value: number) {
        this._minScale = value
        this.validateScale()
    }

    public get minScale(): number {
        return this._minScale
    }

    public set maxWidth(value: number) {
        this._maxWidth = value
        this.validateScale()
    }

    public get maxWidth(): number {
        return this._maxWidth
    }

    public set text(value: string) {
        this._content.text = value
        this.validateScale()
    }

    public get text(): string {
        return this._content.text
    }
}
